// Consign shipments: HHL → CM (or a return CM → HHL), posted at once as stock moves; and confirming one arrived
// (HHL does it for the CM until the portal), where anything short is written off as lost in transit. The lines are the
// usual table (DataTable), quantities typed into their cells.
// The Make plan's "Ship N from HHL" opens the dialog already filled in (ShipFromHhl, `initial`).
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { CmSummary, ShipmentState } from "../../../../shared/consignment.ts";
import type { Item } from "../../../../shared/db.gen.ts";
import { qty } from "../../lib/format.ts";
import { can, mutateWithResult } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { Button, cx, Input, Select, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { toast } from "../../ui/toast.tsx";
import { useCms } from "./lib.ts";
import { ItemPicker, Modal, parseQty } from "./parts.tsx";

type Line = { item: Item; qty: string };
type ShipRow = Line & { id: string; added: number };

export function ShipDialog({
  cm,
  direction,
  initial,
  onClose,
}: {
  cm: CmSummary;
  direction: "to_cm" | "to_hhl";
  /** Lines to start with (the Make plan's parts to ship over). */
  initial?: { item: Item; qty: number }[];
  onClose: () => void;
}) {
  const balances = useRows("stock_balance");
  const locations = useRows("location");
  const hhls = useMemo(() => locations.filter((l) => l.kind === "hhl" && l.active && !l.ignored).sort((a, b) => a.name.localeCompare(b.name)), [locations]);
  const [hhlId, setHhlId] = useState(hhls[0]?.id ?? "");
  const [lines, setLines] = useState<Line[]>(() => (initial ?? []).map((l) => ({ item: l.item, qty: String(l.qty) })));
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [eta, setEta] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [id] = useState(() => crypto.randomUUID()); // one shipment, however many times Ship is pressed
  const toCm = direction === "to_cm";
  const from = toCm ? hhlId : cm.location.id;

  const have = useMemo(() => {
    const out = new Map<string, number>();
    for (const b of balances) if (b.location_id === from) out.set(b.item_id, (out.get(b.item_id) ?? 0) + Number(b.qty));
    return out;
  }, [balances, from]);

  // What the CM's open builds still need that isn't there or on its way (whole units).
  const items = useRows("item");
  const shortfall = useMemo(() => {
    if (!toCm) return [];
    const short = new Map<string, number>();
    for (const o of cm.orders)
      if (o.open) for (const c of o.components) if (c.consigned && c.short > 0) short.set(c.item_id, (short.get(c.item_id) ?? 0) + c.short);
    const byId = new Map(items.map((i) => [i.id, i]));
    return [...short].flatMap(([itemId, n]) => {
      const item = byId.get(itemId);
      return item && item.source === "local" ? [{ item, qty: Math.ceil(n - 1e-9) }] : [];
    });
  }, [cm, toCm, items]);

  const add = (item: Item, n?: number) => setLines((ls) => (ls.some((l) => l.item.id === item.id) ? ls : [...ls, { item, qty: n ? String(n) : "" }]));

  const submit = async () => {
    const bad = lines.find((l) => !((parseQty(l.qty) ?? 0) > 0));
    if (!lines.length) return toast.error("Add something to ship");
    if (bad) return toast.error(`Enter how many ${bad.item.sku || bad.item.name}`);
    setBusy(true);
    const { ok, result } = await mutateWithResult("consign.ship", {
      id,
      direction,
      cm_location_id: cm.location.id,
      hhl_location_id: hhlId || undefined,
      carrier: carrier.trim(),
      tracking: tracking.trim(),
      eta: eta || null,
      notes: notes.trim(),
      lines: lines.map((l) => ({ item_id: l.item.id, qty: parseQty(l.qty) as number })),
    });
    setBusy(false);
    if (!ok) return;
    const r = result as { number: string; warnings?: string[] };
    toast.ok(`${r.number} ${toCm ? `shipped to ${cm.location.name}` : "is on its way back to HHL"}`);
    for (const w of r.warnings ?? []) toast.info(w);
    onClose();
  };

  // The lines: the usual table, the quantity typed into its Ship cell (Enter saves and moves down).
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- in the toolbar
  const rows = useMemo<ShipRow[]>(() => lines.map((l, i) => ({ ...l, id: l.item.id, added: i })), [lines]);
  const setQty = (itemId: string, text: string) => setLines((ls) => ls.map((x) => (x.item.id === itemId ? { ...x, qty: text.trim() } : x)));
  const columns: Column<ShipRow>[] = [
    { key: "photo", header: "", width: "40px", value: () => null, cell: (l) => <Thumb src={l.item.photo_url} size={24} /> },
    { key: "sku", header: "SKU", width: "130px", value: (l) => l.item.sku, cell: (l) => <span className="truncate font-mono text-[12px]">{l.item.sku}</span> },
    { key: "name", header: "Part", width: "minmax(160px,1fr)", value: (l) => l.item.name, cell: (l) => <span className="truncate">{l.item.name}</span> },
    {
      key: "have",
      header: toCm ? "At HHL" : "At CM",
      width: "100px",
      align: "right",
      value: (l) => have.get(l.item.id) ?? 0,
      cell: (l) => {
        const left = (have.get(l.item.id) ?? 0) - (parseQty(l.qty) ?? 0);
        return (
          <span className={cx("font-mono text-[12px]", left < 0 && "text-danger")} title={left < 0 ? "More than the ledger shows there" : undefined}>
            {qty(have.get(l.item.id) ?? 0) || "0"}
          </span>
        );
      },
    },
    {
      key: "qty",
      header: "Ship",
      width: "100px",
      align: "right",
      value: (l) => parseQty(l.qty) ?? l.qty,
      text: (l) => l.qty,
      cell: (l) => <span className={cx("font-mono text-[12px]", !l.qty && "text-faint")}>{l.qty || "—"}</span>,
      edit: { get: (l) => l.qty, save: (l, text) => setQty(l.item.id, text) },
    },
    // The order they were added in (what it's sorted by until someone sorts it another way).
    { key: "added", header: "#", optional: true, width: "48px", align: "right", value: (l) => l.added + 1 },
    {
      key: "remove",
      header: "",
      width: "40px",
      value: () => null,
      cell: (l) => (
        <button
          type="button"
          className="text-faint hover:text-danger"
          title="Remove"
          onClick={(e) => {
            e.stopPropagation();
            setLines((ls) => ls.filter((x) => x.item.id !== l.item.id));
          }}
        >
          ×
        </button>
      ),
    },
  ];

  return (
    <Modal
      title={toCm ? `Ship parts to ${cm.location.name}` : `Return parts from ${cm.location.name} to HHL`}
      onClose={onClose}
      width={860}
      footer={
        <>
          <span className="text-[12px] text-muted">
            {toCm
              ? `Leaves HHL's stock now; counts at ${cm.location.name} as in transit until it's confirmed there.`
              : "Leaves the CM's stock now; in transit until HHL confirms it arrived."}
          </span>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !lines.length} onClick={submit}>
            {toCm ? "Ship" : "Send back"} {lines.length ? `${lines.length} ${lines.length === 1 ? "part" : "parts"}` : ""}
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <ItemPicker
          className="w-80"
          allow={(i) => i.source === "local" && i.kind !== "service"}
          hint={(i) => `${qty(have.get(i.id) ?? 0) || "0"} ${toCm ? "at HHL" : "at the CM"}`}
          onPick={(i) => add(i)}
          placeholder="Add a part: SKU or name…"
        />
        {shortfall.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              for (const s of shortfall) add(s.item, s.qty);
            }}
          >
            Add what open builds are short ({shortfall.length})
          </Button>
        )}
        {hhls.length > 1 && (
          <div className="ml-auto w-52 shrink-0">
            <Select
              aria-label="HHL location"
              value={hhlId}
              onChange={(e) => setHhlId(e.target.value)}
              options={hhls.map((l) => ({ value: l.id, label: `${toCm ? "From" : "To"} ${l.name}` }))}
              className="h-7 text-[12px]"
            />
          </div>
        )}
        <div ref={setSlot} className={cx("flex shrink-0 items-center gap-1", hhls.length <= 1 && "ml-auto")} />
      </div>
      <div className="px-4 pt-2.5">
        <DataTable
          fit={14}
          rows={rows}
          columns={columns}
          editing
          defaultSort={{ key: "added" }}
          rowNoun={["part", "parts"]}
          exportName="Shipment lines"
          viewsSlot={slot}
          pageFilters={false}
          empty={`Search for parts above${shortfall.length ? ", or add what the open builds are short" : ""}.`}
        />
      </div>
      <div className="grid grid-cols-4 gap-2 p-4">
        <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Carrier (e.g. DHL)" aria-label="Carrier" />
        <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number" aria-label="Tracking" />
        <Input type="date" value={eta} onChange={(e) => setEta(e.target.value)} aria-label="Expected" title="Expected to arrive" />
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" aria-label="Notes" />
      </div>
    </Modal>
  );
}

type ArrivedRow = ShipmentState["lines"][number] & { item: Item | undefined };

/**
 * Confirming a shipment in transit arrived, for whoever is at the other end (Consignment → In transit): a dialog next
 * to what opened it, its lines in the usual table with what arrived typed into the Arrived cell (what was sent, until
 * changed).
 */
export function ConfirmArrived({ s, onClose }: { s: ShipmentState; onClose: () => void }) {
  const items = useRows("item");
  const [got, setGot] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- above the lines
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const toCm = s.shipment.direction === "to_cm";
  const rows = useMemo<ArrivedRow[]>(() => s.lines.map((l) => ({ ...l, item: itemById.get(l.item_id) })), [s.lines, itemById]);
  const arrived = (l: ArrivedRow) => got[l.id] ?? String(Number(l.qty));
  const columns: Column<ArrivedRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "130px",
      value: (l) => l.item?.sku ?? "",
      cell: (l) => <span className="truncate font-mono text-[12px]">{l.item?.sku}</span>,
    },
    {
      key: "name",
      header: "Part",
      width: "minmax(160px,1fr)",
      value: (l) => l.item?.name ?? "",
      cell: (l) => <span className="truncate">{l.item?.name}</span>,
    },
    {
      key: "sent",
      header: "Sent",
      width: "90px",
      align: "right",
      value: (l) => Number(l.qty),
      cell: (l) => <span className="font-mono text-[12px] text-muted">{qty(l.qty)}</span>,
    },
    {
      key: "arrived",
      header: "Arrived",
      width: "100px",
      align: "right",
      value: (l) => parseQty(arrived(l)) ?? arrived(l),
      text: (l) => arrived(l),
      cell: (l) => <span className="font-mono text-[12px]">{arrived(l)}</span>,
      edit: { get: arrived, save: (l, text) => setGot((g) => ({ ...g, [l.id]: text.trim() })) },
    },
  ];

  const confirm = async () => {
    const lines = s.lines.flatMap((l) => {
      const text = got[l.id];
      if (text === undefined || text.trim() === "") return [];
      const n = parseQty(text);
      return n == null ? [] : [{ line_id: l.id, qty_received: n }];
    });
    setBusy(true);
    const { ok, result } = await mutateWithResult("consign.receive", { id: s.shipment.id, lines, notes: notes.trim() });
    setBusy(false);
    if (!ok) return;
    const short = (result as { short?: string[] }).short ?? [];
    toast.ok(short.length ? `${s.shipment.number} confirmed; written off as lost: ${short.join(", ")}` : `${s.shipment.number} confirmed`);
    onClose();
  };

  return (
    <Modal
      title={`${s.shipment.number}: confirm arrived ${toCm ? "at the CM" : "at HHL"}`}
      onClose={onClose}
      width={640}
      footer={
        <>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (damaged boxes, who signed)" className="h-7 flex-1 text-[12px]" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={confirm}>
            Confirm {toCm ? "at the CM" : "at HHL"}
          </Button>
        </>
      }
    >
      <div className="flex items-center gap-2 px-4 pt-2.5 pb-1.5 text-[12px] text-muted">
        <span>{toCm ? "To the CM" : "Back to HHL"}</span>
        <div ref={setSlot} className="ml-auto flex items-center gap-1" />
      </div>
      <div className="px-4">
        <DataTable fit rows={rows} columns={columns} editing rowNoun={["part", "parts"]} exportName="Arrived lines" viewsSlot={slot} pageFilters={false} />
      </div>
      <div className="px-4 pt-1.5 pb-3 text-[11px] text-faint">
        Fewer than sent? The difference is written off as lost in transit (a scrap move where it should have landed).
      </div>
    </Modal>
  );
}

/**
 * Make planning --- "Ship N from HHL": the parts a CM's builds take from HHL's stock, in the ship dialog ready to check
 * and send. Only HHL Ops' own parts ship this way: a Shopify item is transferred in Shopify (said, and left out).
 */
export function ShipFromHhl({
  site,
  lines,
  label,
  className,
}: {
  site: string | null;
  lines: { itemId: string; qty: number }[];
  label: string;
  className?: string;
}) {
  const cms = useCms();
  const items = useRows("item");
  const [open, setOpen] = useState(false);
  const cm = site ? cms.get(site) : undefined;
  if (!cm || !can("consign.ship")) return <span className={className}>{label}</span>;
  const byId = new Map(items.map((i) => [i.id, i]));
  const initial = lines.flatMap((l) => {
    const item = byId.get(l.itemId);
    return item && item.source === "local" && item.kind !== "service" && l.qty > 0 ? [{ item, qty: Math.ceil(l.qty - 1e-9) }] : [];
  });
  const start = () => {
    const shopify = lines.flatMap((l) => {
      const item = byId.get(l.itemId);
      return item && item.source !== "local" ? [item.sku || item.name] : [];
    });
    if (shopify.length)
      toast.info(
        `${shopify.join(", ")} ${shopify.length === 1 ? "is a Shopify item" : "are Shopify items"}: transfer ${shopify.length === 1 ? "it" : "them"} to ${cm.location.name} in Shopify`,
      );
    // Nothing HHL Ops can ship itself: the note is all there is to do here.
    if (initial.length) setOpen(true);
    else if (!shopify.length) toast.info("Nothing to ship yet: these parts are still on POs to HHL. Ship them over when they land.");
  };
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          start();
        }}
        className={cx("hover:underline", className)}
        title={`Opens a shipment to ${cm.location.name} with these parts filled in, to check and send`}
      >
        {label}
      </button>
      {open &&
        createPortal(
          // React passes a portal's clicks up its own tree: they stop here, not in the Make plan row behind.
          <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <ShipDialog cm={cm} direction="to_cm" initial={initial} onClose={() => setOpen(false)} />
          </div>,
          document.body,
        )}
    </>
  );
}
